import { z } from "zod";
import type { BisonEnvelope, BisonMessage } from "./bison-contracts.ts";
import { firstQuoteMarker, gmailSignatureSpans, htmlToText } from "./email-html.ts";
import { HEADER_START, isAttribution, isOutlookHeader, ORIGINAL_MESSAGE } from "./quote-markers.ts";
import { type SchedulerLink, schedulerLink } from "./scheduler-links.ts";

/**
 * The lead's own scheduling link (`docs/2026-10-01-lead-scheduling-link.md`; plan step 4, Dawid's
 * decision 4 of 2026-09-30, "Yes, task plus question"). When a lead offers their own booking page,
 * the rep books on it instead of the app sending the seller's link back.
 *
 * This module finds the link: only in the lead's own part of their latest message, above any quoted
 * history; in the body or in the signature; never a page the seller sent. Whether it decides the
 * reply is `move-decision.ts`'s question.
 *
 * Pure and workflow-safe: the drafting pipeline runs it inside a Temporal workflow.
 */

/**
 * The Temporal patches retain historical on/off decisions. New decisions enable the rule
 * everywhere (`temporal/classifier-split-activation.ts`).
 */
export const LEAD_LINK_PATCH = "lead-scheduling-link-v1";
export const LEAD_LINK_OFF_PATCH = "lead-scheduling-link-off-v1";
/**
 * Step 4b (Dawid, about 16:50 CEST 2026-10-01): a link only in the signature is an offer only when the
 * lead's words point to it. Asked before `-v1` for a signature link, so a history recorded before 4b
 * answers false and keeps the `-v1` decision; new decisions enable it everywhere.
 */
export const LEAD_LINK_WORDS_PATCH = "lead-scheduling-link-v2";

/** The run action a task closes with; `run-projection.ts` turns it into the rep's step. */
export const LEAD_LINK_ACTION = "book_on_lead_link";

/**
 * The rep's step for a task is a `manual` step whose id is
 * `lead-link-<placement>-r<conversation revision>-c<config revision>`; the id is what tells it from any
 * other chore, in the inbox and on the page.
 */
export const LEAD_LINK_STEP_PREFIX = "lead-link";

const LeadLinkPlacementSchema = z.enum(["body", "signature"]);
export type LeadLinkPlacement = z.infer<typeof LeadLinkPlacementSchema>;

/**
 * What a run records, in its own sidecar file (`run-persistence.ts`), only where the rule applied or
 * the rep lifted it: `applied` closed the turn as a task; `lifted` drafted it because the rep chose
 * "Send our link". `cut` says how the signature was told from the body.
 */
export const LeadLinkRecordSchema = z.strictObject({
  version: z.literal(1),
  state: z.enum(["applied", "lifted"]),
  url: z.string().regex(/^https:\/\/[a-z0-9.-]+(?:\/\S*)?$/u),
  scheduler: z.enum(["calendly", "hubspot", "google", "microsoft", "other"]),
  placement: LeadLinkPlacementSchema,
  cut: z.enum(["gmail_marker", "delimiter", "closing", "contact_block", "none"]),
  links_found: z.number().int().positive(),
  evidence_id: z.string().min(1),
});
export type LeadLinkRecord = z.infer<typeof LeadLinkRecordSchema>;
/**
 * What the detector found. `pointed` says the lead's own words, above the signature cut, point to
 * their link or calendar (`pointsToOwnLink`); the run's record never carries it.
 */
export type LeadLinkFinding = Omit<LeadLinkRecord, "version" | "state"> & { pointed: boolean };
type Cut = LeadLinkRecord["cut"];

/** No message makes the workflow task slow: a body past this many characters is not read. */
const BODY_BOUND = 100_000;

/** A link as text shows it: `<url>` after a label, a bare URL, or a host and path typed without a scheme. */
const TEXT_LINK =
  /<(https?:\/\/[^>\s]+)>|\bhttps?:\/\/[^\s<>"')\]]+|\b(?:[a-z0-9-]+\.)+[a-z]{2,}\/[^\s<>"')\]]*/giu;
const ANCHOR = /<a\b[^>]*?\bhref\s*=\s*["']([^"']+)["'][^>]*>[\s\S]*?<\/a>/giu;

function linksIn(text: string): string[] {
  return [...text.matchAll(TEXT_LINK)].map((match) => match[1] ?? match[0]);
}

/**
 * An HTML part as lines of text, each link where its anchor stood as `<href>`. The anchor's own words
 * are dropped: a link is where it points, never what it shows.
 */
function annotatedText(html: string): string {
  return htmlToText(html.replace(ANCHOR, (_, href: string) => ` &lt;${href}&gt; `));
}

const CLOSINGS = [
  "best",
  "best regards",
  "best wishes",
  "kind regards",
  "warm regards",
  "regards",
  "warmly",
  "thanks",
  "thank you",
  "many thanks",
  "thanks again",
  "cheers",
  "sincerely",
  "all the best",
  "talk soon",
  "speak soon",
  "mit freundlichen grüßen",
  "viele grüße",
  "beste grüße",
  "liebe grüße",
  "pozdrawiam",
  "pozdrowienia",
  "z poważaniem",
  "cordialement",
  "saludos",
  "un saludo",
  "groeten",
  "met vriendelijke groet",
  "med vänlig hälsning",
].sort((a, b) => b.length - a.length);
const CLOSING_PHRASE = new RegExp(`^(?:${CLOSINGS.join("|")})(?=$|[\\s,.!\\-–—])`, "iu");

/** A sign-off on its own line: "Best,", "Thanks!", "Cheers - Morgan". Never "Thanks for …". */
function isClosing(line: string): boolean {
  const phrase = CLOSING_PHRASE.exec(line);
  if (!phrase) return false;
  const rest = line.slice(phrase[0].length).replace(/^[,.!]*\s*[-–—]?\s*/u, "");
  return rest === "" || /^\p{Lu}[\p{L}'.-]*(?:\s+\p{Lu}[\p{L}'.-]*){0,2}$/u.test(rest);
}

/** The lines before any quoted history: an attribution, a separator, an Outlook header, a `>` line. */
function aboveQuotedHistory(text: string): string[] {
  const lines = text
    .slice(0, BODY_BOUND)
    .split("\n")
    .map((line) => line.trim());
  const quote = lines.findIndex(
    (line, index) =>
      line.startsWith(">") ||
      isAttribution(line) ||
      ORIGINAL_MESSAGE.test(line) ||
      (HEADER_START.test(line) && isOutlookHeader(lines.slice(index, index + 8).join("\n"))),
  );
  return quote === -1 ? lines : lines.slice(0, quote);
}

/**
 * The heuristic signature cut for text with no Gmail marker, first by position: a `--` delimiter or a
 * closing line after some words; failing both, a last paragraph of short contact lines. Its accuracy
 * outside Gmail is unmeasured (`docs/2026-10-01-lead-scheduling-link.md`).
 */
function textRegions(text: string): Reading {
  const lines = aboveQuotedHistory(text);
  const delimiter = lines.findIndex((line) => line === "--" || line === "—");
  const firstWords = lines.findIndex(Boolean);
  const closing =
    firstWords === -1
      ? -1
      : lines.findIndex((line, index) => index > firstWords && isClosing(line));
  const marked = [
    ...(delimiter === -1 ? [] : [{ at: delimiter, cut: "delimiter" as const }]),
    ...(closing === -1 ? [] : [{ at: closing, cut: "closing" as const }]),
  ].sort((a, b) => a.at - b.at)[0];
  const split = (at: number, cut: Cut): Reading => {
    const words = lines.slice(0, at).join("\n");
    return { cut, words, body: linksIn(words), signature: linksIn(lines.slice(at).join("\n")) };
  };
  if (marked) return split(marked.at, marked.cut);
  const lastBlank = lines.findLastIndex((line, index) => line === "" && index < lines.length - 1);
  const block = lastBlank === -1 ? [] : lines.slice(lastBlank + 1).filter(Boolean);
  const contactBlock =
    lines.slice(0, Math.max(lastBlank, 0)).some(Boolean) &&
    block.length >= 2 &&
    block.every((line) => line.length <= 80 && !/[?:]$/u.test(line));
  if (contactBlock) return split(lastBlank + 1, "contact_block");
  return split(lines.length, "none");
}

/** The trigger's own part: how it was cut, the lead's words above the cut, and the links on each side. */
type Reading = { cut: Cut; words: string; body: string[]; signature: string[] };

/**
 * The trigger's own part, its links sorted into body and signature. The HTML part is read when it
 * marks where the quote starts, or when the extractor found no quote at all, because only the HTML
 * keeps a link hidden behind words; otherwise the extractor's authored text is read.
 */
function readTrigger(input: BisonEnvelope, trigger: BisonMessage): Reading | null {
  const authored = trigger.authored_body;
  if (authored.text === null) return null;
  const record = input.source_records.find(
    (row) => row.source.record_id === trigger.provider_id,
  )?.record;
  const rawHtml = record?.["html_body"];
  const html = typeof rawHtml === "string" && rawHtml.trim() ? rawHtml.slice(0, BODY_BOUND) : null;
  const marker = html === null ? null : firstQuoteMarker(html);
  if (html === null || (marker === null && authored.status !== "unchanged"))
    return textRegions(authored.text);
  const own = marker === null ? html : html.slice(0, marker.index);
  const spans = gmailSignatureSpans(own);
  if (spans.length === 0) return textRegions(annotatedText(own));
  let body = "";
  let from = 0;
  for (const span of spans) {
    body += own.slice(from, span.start);
    from = span.end;
  }
  body += own.slice(from);
  const words = annotatedText(body);
  return {
    cut: "gmail_marker",
    words,
    body: linksIn(words),
    signature: spans.flatMap((span) => linksIn(annotatedText(own.slice(span.start, span.end)))),
  };
}

/**
 * What makes "in my signature" about a booking link: "link in my signature" is, "my number is in my
 * signature" is not, so "call" is left out here.
 */
const SIGNATURE_BOOKING =
  /\b(?:link|calendar|calendly|diary|book|booking|schedule|scheduling|slot|meet|meeting|appointment)\b/u;
/** What makes "the link below" a booking link and not, say, an event's registration link. */
const LINK_BOOKING =
  /\b(?:calendar|calendly|diary|book|booking|schedule|scheduling|slot|meet|meeting|appointment|call|chat|availability)\b/u;
/**
 * One shape of words pointing to the lead's own link or calendar: the pattern, and the word the
 * sentence must also carry, if any.
 */
type Pointing = { pattern: RegExp; booking: RegExp | null };
/**
 * Shapes that point to the lead's own link or calendar, read in their words above the signature cut
 * (`docs/2026-10-01-lead-scheduling-link.md`, "Step 4b"). Built for precision from the shapes the
 * corpus's lead replies use: each needs a possessive ("my"), a place ("below", "in my signature") or an
 * invitation, so "I'll check my calendar" and "send me your calendar link" never count, and "my number
 * is in my signature" lacks the booking word it needs.
 */
const POINTING: readonly Pointing[] = [
  // "link in my signature", "the link to schedule a time in my email signature below"
  {
    pattern:
      /\b(?:in|on|from|via|using|through|under|at\s+the\s+bottom\s+of)\s+my\s+(?:e-?mail\s+)?signature\b/u,
    booking: SIGNATURE_BOOKING,
  },
  // "my Calendly", "my booking link", "here's my calendar link", "a link to my calendar"
  {
    pattern:
      /\bmy\s+(?:calendly|booking\s+(?:link|page)|scheduling\s+(?:link|page)|(?:calendar|diary)\s+link)\b/u,
    booking: null,
  },
  { pattern: /\blink\s+to\s+my\s+(?:calendar|diary|calendly|agenda)\b/u, booking: null },
  { pattern: /\bhere(?:'s|’s|\s+is)\s+my\s+(?:calendar|diary|calendly)\b/u, booking: null },
  // "the link below to schedule a call", "my calendar below", "my availability below"
  {
    pattern:
      /\b(?:(?:the|my|this)\s+link|calendar|calendly|diary|my\s+availability)\s+(?:is\s+)?(?:below|underneath|beneath)\b/u,
    booking: LINK_BOOKING,
  },
  {
    pattern:
      /\b(?:book|schedule|grab|pick|choose|select|reserve)\s+(?:a\s+|any\s+|some\s+)?(?:convenient\s+|suitable\s+)?(?:time|slot|call|meeting)\s+(?:below|underneath|beneath)\b/u,
    booking: null,
  },
  // an invitation to book on their calendar: "grab time from my calendar", "jump on my Calendly"
  {
    pattern:
      /\b(?:book|schedule|grab|pick|choose|select|reserve|put|jump\s+on|get\s+on|access)\b[^.!?]{0,40}?\bmy\s+(?:calendar|diary|agenda|calendly)\b/u,
    booking: null,
  },
  {
    pattern:
      /\b(?:feel\s+free\s+to|please|you\s+can|you\s+could)\s+(?:check|see|view|look\s+at|use)\s+my\s+(?:calendar|diary|availability)\b/u,
    booking: null,
  },
  // "signature" in the other languages the corpus's leads write, beside a booking word in that language
  {
    pattern:
      /\b(?:assinatura|firma|signatur|handtekening|podpisie|stopce|stopka)\b[^.!?]{0,60}?\b(?:calend[aá]rio|agenda|link|kalender|kalendarz|calendrier|lien|enlace|termin)\b|\b(?:calend[aá]rio|agenda|link|kalender|kalendarz|calendrier|lien|enlace|termin)\b[^.!?]{0,60}?\b(?:assinatura|firma|signatur|handtekening|podpisie|stopce|stopka)\b/u,
    booking: null,
  },
];
/** A sentence that opens with the lead's own plan ("I'll check my calendar") invites nobody. */
const OWN_PLAN = /^(?:i|i'll|i’ll|i\s+will|i'd|i\s+would|let\s+me|i\s+can|i\s+need\s+to)\b/u;

/**
 * Whether the lead's own words point to their own booking link or calendar: "link in my signature",
 * "grab a time on my calendar", "use the link below to schedule a call". Host code, no model. The
 * caller passes only the authored part above the signature cut, so a signature's "Book a meeting with
 * me" never counts. A sentence runs across hard-wrapped lines and ends at a full stop, a question or
 * exclamation mark, or a blank line.
 */
function pointsToOwnLink(words: string): boolean {
  const sentences = words
    .slice(0, BODY_BOUND)
    .toLowerCase()
    .replace(/<[^>\s]+>|\bhttps?:\/\/\S+/gu, " ")
    .split(/[.!?]+|\n\s*\n/u)
    .map((sentence) => sentence.replace(/\s+/gu, " ").trim())
    .filter(Boolean);
  return sentences.some(
    (sentence) =>
      !OWN_PLAN.test(sentence) &&
      POINTING.some(
        ({ pattern, booking }) =>
          pattern.test(sentence) && (booking === null || booking.test(sentence)),
      ),
  );
}

/** Every value in a JSON-shaped record, depth-bounded, as strings. */
function stringsIn(value: unknown, depth = 0): string[] {
  if (typeof value === "string") return [value];
  if (depth > 6 || value === null || typeof value !== "object") return [];
  return Object.values(value).flatMap((entry) => stringsIn(entry, depth + 1));
}

/**
 * Whose pages the seller has used: every scheduling link in the thread's outbound messages (text and
 * raw parts, which catches an `href`), the sending mailbox's signature, the campaign's sequence copy
 * and its booking link. Compared by owner, so another event of the seller's Calendly user is the
 * seller's too, and the copy covers Bison rewriting the thread's links for tracking.
 */
function sellerOwners(input: BisonEnvelope, bookingUrl: string | null): Set<string> {
  const outbound = input.messages.filter((message) => message.direction === "outbound");
  const outboundIds = new Set(outbound.map((message) => message.provider_id));
  const texts = [
    ...outbound.map((message) => message.text),
    ...input.source_records
      .filter((row) => outboundIds.has(row.source.record_id))
      .flatMap((row) => stringsIn(row.record)),
    ...(typeof input.sender.email_signature === "string" ? [input.sender.email_signature] : []),
    ...stringsIn(input.campaign_sequence),
    ...(bookingUrl ? [bookingUrl] : []),
  ];
  return new Set(
    texts
      .flatMap(linksIn)
      .map(schedulerLink)
      .flatMap((link) => (link ? [link.owner] : [])),
  );
}

/**
 * The lead's own scheduling link in their latest message, or null. Nothing is decided where the
 * extractor could not separate the lead's words from the quote, or found none. A link in the body
 * wins over one in the signature; among equals, the first.
 */
export function findLeadSchedulingLink(
  input: BisonEnvelope,
  bookingUrl: string | null,
): LeadLinkFinding | null {
  const trigger = input.messages.find(
    (message) => message.provider_id === input.trigger.id && message.direction === "inbound",
  );
  if (!trigger) return null;
  if (trigger.authored_body.status === "ambiguous") return null;
  if (trigger.authored_body.status === "no_authored_content") return null;
  const reading = readTrigger(input, trigger);
  if (!reading) return null;
  const seller = sellerOwners(input, bookingUrl);
  const pages = (links: string[]): SchedulerLink[] =>
    links.flatMap((raw) => {
      const link = schedulerLink(raw);
      return link && !seller.has(link.owner) ? [link] : [];
    });
  const body = pages(reading.body);
  const signature = pages(reading.signature);
  const chosen = body[0] ?? signature[0];
  if (!chosen) return null;
  return {
    url: chosen.url,
    scheduler: chosen.kind,
    placement: body[0] ? "body" : "signature",
    cut: reading.cut,
    links_found: new Set([...body, ...signature].map((link) => link.url)).size,
    evidence_id: trigger.id,
    pointed: pointsToOwnLink(reading.words),
  };
}

/**
 * The trigger's own words above its signature cut, as the lead-link rule reads them: the HTML part
 * when it marks the quote or a Gmail signature, else the extractor's authored text. Null when the
 * extractor could not separate the lead's words. The conditional-no rule reads the same words, so a
 * disclaimer's "If you are not the intended recipient" never counts (`conditional-no.ts`).
 */
export function triggerOwnWords(input: BisonEnvelope): string | null {
  const trigger = input.messages.find(
    (message) => message.provider_id === input.trigger.id && message.direction === "inbound",
  );
  if (!trigger) return null;
  if (trigger.authored_body.status === "ambiguous") return null;
  if (trigger.authored_body.status === "no_authored_content") return null;
  return readTrigger(input, trigger)?.words ?? null;
}

/** The plain-text path of `triggerOwnWords`, for text with no envelope (evaluation pools). */
export function ownWordsOfText(text: string): string {
  return textRegions(text).words;
}

/** The description the run's action carries: what an older build shows as the step's text. */
export function leadLinkActionDescription(record: Pick<LeadLinkRecord, "placement">): string {
  return record.placement === "body"
    ? "Book on their link: the lead offered their own booking page, so nothing was drafted. Open their page, book the call for the call host, then mark it booked."
    : "Their booking page is in their signature, so nothing was drafted. Book on their link, or send our link instead.";
}

/** Where the link of an open lead-link step was found, or null for any other step. */
export function leadLinkStepPlacement(step: {
  id: string;
  input: { kind: string };
}): LeadLinkPlacement | null {
  if (step.input.kind !== "manual") return null;
  const match = /^lead-link-([a-z]+)-r[0-9]+-c[0-9]+$/u.exec(step.id);
  const placement = LeadLinkPlacementSchema.safeParse(match?.[1]);
  return placement.success ? placement.data : null;
}
